/**
 * PORTED — RO-3-00g / #44474.
 * Origin: `excellent/app/issues/_unified-list.tsx`.
 *
 * Changes:
 *   next/navigation → @excellent/ui/router
 *   submitVerdictAction / approvePromotionFromQueue / rejectPromotionFromQueue
 *     → apiCall(verdicts.*) (RO-2-09e)
 *   verifyActionFromUi / rejectActionFromUi → apiCall(agents.actionVerify|Reject) (RO-2-11)
 *   task review + run verdict accept/reject/edit → the ONE shared handler module
 *     `../work/review-decision` (verification-loop P2-U1) — both legs funnel the
 *     server's `override.human` capture; the expanded run verdict renders the
 *     shared `<Decision>` block, not page-local buttons.
 *
 * `@/work/rows`, `@/work/issues-display`, `@/work/types`, `@/projects/types` stay
 * via the `@/` alias — pure modules, no server sink.
 */

import * as React from "react";
import { useState, useTransition } from "react";
import { useNavigate, usePathname, useSearch } from "@excellent/ui/router";
import {
  WorkList,
  WorkGroup,
  columnsFor,
  Decision,
  VerdictCard,
  PromotionGateCard,
  Card,
  CardContent,
  scopedTaskToRow,
  roleLabel,
  type WorkRowModel,
} from "@excellent/ui";
import { Check, X, Gauge, ArrowUpCircle, Zap } from "@excellent/ui/icons";
import { workItemToRow } from "@/work/rows";
import {
  groupTasks,
  orderTasks,
  resolveIssuesDisplay,
  type IssueProperty,
} from "@/work/issues-display";
import type { WorkItem } from "@/work/types";
import type { ScopedTask, ViewScope } from "@/projects/types";
import { apiCall } from "@excellent/ui/api";
// Alias: the prop below is also named `verdicts` and would shadow the contract.
import { verdicts as verdictsApi } from "@excellent/ui/api/contracts/verdicts";
import { agents } from "@excellent/ui/api/contracts/agents";
import {
  decideTaskReview,
  decideWorkerRun,
  promptDecisionReason,
} from "../work/review-decision";

/**
 * The unified Work list on the Issues page (Matt, 2026-07-16 — "they are work …
 * incorporated as issues on that board. Work is a consolidation of all of the
 * things the user needs to do or has in flight").
 *
 * ONE lifecycle-row list over every source, all in the SAME row so they're
 * indistinguishable in format:
 *   • tasks    — the scoped issue read (all statuses) via `scopedTaskToRow`; a
 *                task in `review` gains the Accept/Reject verify decision.
 *   • extras   — non-task attention (escalations, health, gates, mentions) via
 *                `workItemToRow` — the same mapping the Inbox uses. Deduped
 *                against tasks already shown; a severity dot leads them.
 *   • verdicts — optimizer decisions (loop-run judgments + promotion launch
 *                gates + action approvals). Accept/Reject on the row; clicking a
 *                run/promotion expands its VerdictCard / PromotionGateCard —
 *                the ship/verify + Crucible decisions, folded in (Matt 07-16).
 *
 * A task/extra opens the PeekDialog quick-view in place (Expand → its full
 * page) or navigates; a verdict expands inline.
 */

// Local mirror of the server ReviewItem union's non-task members (avoid importing
// a server-only module's VALUES into a client component; the page passes these as
// serializable props). Shapes match `@/review/inbox`.
type RunVerdict = {
  kind: "run";
  id: number;
  loop_slug: string | null;
  role: string;
  model: string | null;
  outcome: string | null;
  judge_score: number | null;
  started_at: string;
  output_excerpt: string | null;
  why: string;
};
type PromotionVerdict = {
  kind: "promotion";
  id: number;
  loop_slug: string;
  step_name: string;
  from_rung: number;
  to_rung: number;
  artifact_path: string | null;
  artifact_hash: string | null;
  state: "proposed" | "shadow" | "active" | "demoted" | "rolled_back";
  drift: number | null;
  why: string;
};
type ActionVerdict = {
  kind: "action";
  id: number;
  uid: string;
  title: string;
  action_kind: string;
  risk: "low" | "high";
  status: "proposed" | "verified";
  requires_human: boolean;
  min_verifiers: number;
  proposed_by: string | null;
  created_at: string;
};
export type ReviewVerdict = RunVerdict | PromotionVerdict | ActionVerdict;

/** One render entry — a row, plus the card it expands into (verdicts only). */
interface Entry {
  row: WorkRowModel;
  expand?: React.ReactNode;
}

export function UnifiedIssuesList({
  tasks,
  extras,
  verdicts = [],
  scope = { kind: "workspace" },
  empty = "No issues match your filters.",
  sectioned = false,
}: {
  tasks: ScopedTask[];
  extras: WorkItem[];
  /** Optimizer decisions folded into the list (runs + promotions + actions). */
  verdicts?: ReviewVerdict[];
  scope?: ViewScope;
  empty?: React.ReactNode;
  /**
   * Render each group as its OWN collapsible Card section (the Goals-ledger
   * treatment — chevron dropdown + count + a `+` that creates an issue in that
   * status), rather than one flat card of caption-separated groups. Set by the
   * Work ▸ Issues editorial mount; the standalone `/issues` keeps the flat list.
   */
  sectioned?: boolean;
}) {
  const router = useNavigate();
  const pathname = usePathname();
  const search = useSearch();
  const [, start] = useTransition();
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // A clicked task opens the shared task peek in place via `?task=<id>` — the
  // ONE doorway every task surface writes to, rendered by the global
  // `TaskPeekHost` in the root layout. (This list used to hold the peek in
  // local state while the drawer still owned `?task=`; now that the host IS the
  // peek, there's one path and deep links work from here too.)
  const openTask = React.useCallback(
    (id: number) => {
      const next = new URLSearchParams(search?.toString());
      next.set("task", String(id));
      router.replace(`${pathname}?${next.toString()}`);
    },
    [router, pathname, search],
  );

  const openExtra = React.useCallback(
    (item: WorkItem) => {
      if (item.taskId != null) return openTask(item.taskId);
      if (item.href) router.push(item.href);
    },
    [router, openTask],
  );

  // A verdict decision — run the server action, then refresh; surface failures.
  const act = (fn: () => Promise<unknown>) =>
    start(async () => {
      setMsg(null);
      try {
        await fn();
        router.refresh();
      } catch (e) {
        setMsg(`Failed: ${(e as Error)?.message ?? "unknown"}`);
      }
    });
  const toggle = (key: string) => setExpanded((cur) => (cur === key ? null : key));

  // Dedup: an alert bound to a task ALREADY in the list is redundant — the task
  // row is the single source (Matt 07-16). Keep task-less alerts.
  const visibleTaskIds = new Set(tasks.map((t) => t.id));
  const shownExtras = extras.filter((e) => e.taskId == null || !visibleTaskIds.has(e.taskId));

  // ── Verdict rows (decisions lead the list) ────────────────────────────────
  // A verdict awaiting your accept/reject has STOPPED on you — the textbook
  // blocked row — so each decidable one carries the blocked label + edge bar
  // (#43867), and its kind glyph (a gauge for an optimizer run, an up-arrow for a
  // promotion gate, a bolt for an action) leads the row where a task's ring would.
  const verdictEntry = (v: ReviewVerdict): Entry => {
    if (v.kind === "run") {
      const key = `run-${v.id}`;
      return {
        row: {
          id: key,
          signifier: { as: "glyph", icon: Gauge, label: "Optimizer run" },
          title: `#${v.id} ${v.loop_slug ? `${v.loop_slug} · ${roleLabel(v.role)}` : roleLabel(v.role)}`,
          subtitle: [v.why, v.output_excerpt].filter(Boolean).join(" — ") || undefined,
          attention: {
            label: "Needs your decision",
            level: v.outcome === "fail" ? "danger" : "warning",
            blocked: true,
          },
          actions: [
            { icon: Check, label: "Accept", onClick: () => act(() => decideWorkerRun({ runId: v.id, decision: "accept" })) },
            {
              icon: X,
              label: "Reject",
              onClick: () => {
                const reason = promptDecisionReason("reject");
                if (reason === undefined) return;
                act(() => decideWorkerRun({ runId: v.id, decision: "reject", note: reason }));
              },
            },
          ],
          onOpen: () => toggle(key),
        },
        // The expanded run renders the SHARED Decision block (accept/reject/edit
        // through the one override.human path) — the card itself is the evidence.
        expand: (
          <VerdictCard
            run={v}
            actions={
              <Decision
                className="w-full"
                acceptLabel="Accept"
                rejectLabel="Reject"
                onAccept={() => act(() => decideWorkerRun({ runId: v.id, decision: "accept" }))}
                onReject={() => {
                  const reason = promptDecisionReason("reject");
                  if (reason === undefined) return;
                  act(() => decideWorkerRun({ runId: v.id, decision: "reject", note: reason }));
                }}
                onEdit={() => {
                  const edited = promptDecisionReason("edit");
                  if (edited === undefined || edited.trim() === "") return;
                  act(() => decideWorkerRun({ runId: v.id, decision: "edit", editedOutput: edited }));
                }}
              />
            }
          />
        ),
      };
    }
    if (v.kind === "promotion") {
      const key = `promotion-${v.id}`;
      const decidable = v.state === "proposed" || v.state === "shadow";
      return {
        row: {
          id: key,
          signifier: { as: "glyph", icon: ArrowUpCircle, label: "Promotion gate" },
          title: `Promote ${v.step_name}`,
          subtitle: v.why || undefined,
          // Only a decidable promotion is waiting on you; an already-active or
          // rolled-back one is informational.
          attention: decidable
            ? { label: "Needs your decision", level: "warning", blocked: true }
            : undefined,
          actions: decidable
            ? [
                {
                  icon: Check,
                  label: "Approve",
                  onClick: () => {
                    if (
                      confirm(
                        `Approve promoting “${v.step_name}” to a rung-④ script? Changes production (drift-monitored, reversible).`,
                      )
                    )
                      act(() => apiCall(verdictsApi.approvePromotion, { body: { promotionId: v.id } }));
                  },
                },
                { icon: X, label: "Reject", onClick: () => act(() => apiCall(verdictsApi.rejectPromotion, { body: { promotionId: v.id } })) },
              ]
            : [],
          onOpen: () => toggle(key),
        },
        expand: <PromotionGateCard promotion={v} />,
      };
    }
    // action approval
    const meta = [
      v.requires_human ? "human required" : null,
      v.min_verifiers > 1 ? `${v.min_verifiers} verifiers` : null,
      v.proposed_by ? `by ${v.proposed_by}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return {
      row: {
        id: `action-${v.id}`,
        signifier: { as: "glyph", icon: Zap, label: "Action" },
        state: { as: "status", status: v.status === "verified" ? "done" : "review" },
        title: v.title,
        subtitle: meta || undefined,
        // High risk raises the tone rather than prefixing a ⚠ glyph onto the
        // title — the mark is where "a person is needed, and how loud" belongs.
        attention:
          v.status === "proposed"
            ? { label: "Needs your decision", level: v.risk === "high" ? "danger" : "warning", blocked: true }
            : undefined,
        actions:
          v.status === "proposed"
            ? [
                {
                  icon: Check,
                  label: "Accept",
                  onClick: () =>
                    act(async () => {
                      const r = await apiCall(agents.actionVerify, { params: { uid: v.uid } });
                      if (!r.verified) setMsg(`Recorded — still ${r.reason.replace(/-/g, " ")}`);
                    }),
                },
                {
                  icon: X,
                  label: "Reject",
                  onClick: () => {
                    const note = prompt("Reason for rejecting this action:") ?? undefined;
                    act(() => apiCall(agents.actionReject, { params: { uid: v.uid }, body: { note } }));
                  },
                },
              ]
            : [],
      },
    };
  };

  // ── Task rows (a review-status task gains the Accept/Reject verify decision,
  // through the ONE shared handler → override.human path — P2-U1) ─────────────
  const taskEntry = (t: ScopedTask): Entry => {
    const row = scopedTaskToRow(t, { scope, onOpen: openTask });
    if (t.status === "review") {
      const base = { taskId: t.id, projectSlug: t.project_slug, sectionSlug: t.section_slug };
      row.actions = [
        { icon: Check, label: "Accept", onClick: () => act(() => decideTaskReview({ ...base, decision: "accept" })) },
        {
          icon: X,
          label: "Reject",
          onClick: () => {
            const reason = promptDecisionReason("reject");
            if (reason === undefined) return;
            act(() => decideTaskReview({ ...base, decision: "reject", reason }));
          },
        },
      ];
    }
    return { row };
  };

  // ── Display state (grouping · ordering · visible columns) ─────────────────
  // Written by the <DisplayMenu> in the title row, read here off the URL — the
  // two never talk, so `@/work/issues-display` is the single vocabulary.
  const display = resolveIssuesDisplay((k) => search?.get(k));

  // Hiding a column is done by STRIPPING the field from the row model, not by
  // teaching WorkRow six visibility props: the model has exactly one optional
  // field per column, so absence is the existing "don't render".
  const trim = (row: WorkRowModel, keep: IssueProperty[]): WorkRowModel => {
    const has = (p: IssueProperty) => keep.includes(p);
    // scopedTaskToRow emits ONE tag (the project at the workspace tier, else the
    // section); keep it iff its dimension is on. Match on the pill's own `hint`,
    // not an index, since the row carries only the one WorkRow renders.
    const pill = row.pills?.[0];
    const keepPill = !!pill && (pill.hint === "Project" ? has("project") : has("section"));
    return {
      ...row,
      state: has("status") ? row.state : undefined,
      // Only the PRIORITY signifier is a task column; a verdict's kind glyph is
      // its identity and survives every toggle.
      signifier: row.signifier?.as === "priority" && !has("priority") ? undefined : row.signifier,
      pills: keepPill ? [pill!] : undefined,
      owner: has("assignee") ? row.owner : undefined,
      date: has("date") ? row.date : undefined,
      // `attention` (the blocked mark) is not a column — it answers "is anyone
      // needed", which the property toggles don't govern — so it always rides.
    };
  };

  // Decisions and attention are NOT issues — they have no status pipeline, no
  // assignee, no project — so they lead the list as a flat block and sit outside
  // grouping/ordering. Only the issue queue below them is laid out.
  const leading: Entry[] = [
    ...verdicts.map(verdictEntry),
    ...shownExtras.map((i) => ({ row: workItemToRow(i, openExtra) })),
  ];
  const groups = groupTasks(
    orderTasks(tasks, display.ordering),
    display.grouping,
    display.showEmpty,
  );
  const total = leading.length + tasks.length;

  // Build every entry once, then derive the reserved trailing columns from the
  // WHOLE visible set — so a verdict row (no owner/date) and a task row (both)
  // put their avatars and stamps on the same vertical rule instead of each
  // list-fragment aligning to itself. This is what WorkRow's `columns` prop is
  // for: alignment is a property of the list, not of the row.
  const groupEntries = groups.map((g) => ({ ...g, entries: g.tasks.map(taskEntry) }));
  const cols = columnsFor(
    [...leading, ...groupEntries.flatMap((g) => g.entries)].map((e) => trim(e.row, display.properties)),
  );

  // Entry → WorkRowModel (trimmed + its inline expansion when open). ONE mapping,
  // so every list below is a plain <WorkList> whose rows are DIRECT siblings —
  // which is what restores the row lines. The old `renderEntry` wrapped each
  // WorkRow in its own `<div key>`, making every row the "last" child of its own
  // wrapper, so `last:border-b-0` matched every row and erased every divider
  // (Matt, 2026-07-23: "most pages are missing the lines between the items").
  // The verdict cards that used to force that wrapper now ride `model.expansion`,
  // which WorkList renders as the row's sibling.
  const toModel = (e: Entry): WorkRowModel => ({
    ...trim(e.row, display.properties),
    expansion: e.expand && expanded === e.row.id ? e.expand : undefined,
  });
  const leadingRows = leading.map(toModel);

  // ── Sectioned layout (Work ▸ Issues) — one collapsible Card per group ──────
  // Each status group is its OWN collapsible WorkGroup (chevron + count pill +
  // tinted header + a `+` that opens the composer preset to that status). The
  // leading decisions/attention block, which has no status of its own, rides a
  // plain card above the pipeline.
  if (sectioned) {
    if (total === 0) {
      return (
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="p-0">
            <WorkList rows={[]} gutter empty={empty} />
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {msg ? <p className="px-1 type-subtext text-muted-foreground">{msg}</p> : null}
        {leading.length > 0 ? (
          // The decisions/attention lead-in is its OWN titled section — the same
          // collapsible WorkGroup the status buckets use — rather than an
          // unlabelled card. Every row here is by definition waiting on the reader
          // (a mention to reply to, a stuck run, a verdict to decide), so the
          // section is named for that (Matt, 2026-07-23: "evolve this into a
          // section"). It leads because a person is needed; the status pipeline
          // follows.
          <WorkGroup title="Needs you" count={leading.length}>
            <WorkList rows={leadingRows} columns={cols} gutter />
          </WorkGroup>
        ) : null}
        {groupEntries.map((g) =>
          // An unlabelled group ("No grouping") has no header chrome to hang — a
          // plain card, like the leading block. A labelled status group is the
          // canonical WorkGroup: its chevron, count pill, tinted header strip and
          // `+` are the ONE container, retiring the hand-rolled IssueSection this
          // list used to carry.
          g.label ? (
            <WorkGroup
              key={g.key}
              title={g.label}
              count={g.tasks.length}
              // The `+` creates in that status — only when the groups ARE statuses
              // (assignee/project groups have no status to seed); never for the
              // terminal `cancelled` bucket (creating a cancelled issue is noise).
              onAdd={
                display.grouping === "status" && g.key !== "cancelled"
                  ? () => dispatchCreateInStatus(g.key)
                  : undefined
              }
              addLabel={`New issue in ${g.label}`}
            >
              <WorkList
                rows={g.entries.map(toModel)}
                columns={cols}
                gutter
                empty="Nothing in this group."
              />
            </WorkGroup>
          ) : (
            <Card key={g.key} className="gap-0 overflow-hidden py-0">
              <CardContent className="p-0">
                <WorkList rows={g.entries.map(toModel)} columns={cols} gutter />
              </CardContent>
            </Card>
          ),
        )}
      </div>
    );
  }

  // ── Flat layout (standalone /issues) — card-less; the page supplies the Card.
  // Same WorkList rows (so the dividers are back here too); a labelled group gets
  // the light caption strip that reads inside the page's single card.
  if (total === 0) {
    return <WorkList rows={[]} gutter empty={empty} />;
  }
  return (
    <div>
      {msg ? <p className="px-4 pt-2 type-subtext text-muted-foreground">{msg}</p> : null}
      {leadingRows.length > 0 ? <WorkList rows={leadingRows} columns={cols} gutter /> : null}
      {groupEntries.map((g) => (
        <div key={g.key}>
          {/* "No grouping" yields one unlabelled group, so the list renders
              exactly as it did before this control existed. The label strip aligns
              to the row gutter (px-4) and carries the same tint as a WorkGroup
              header, so grouped rows read the same flat and sectioned. */}
          {g.label ? (
            <div className="flex items-center gap-2 border-b border-border bg-foreground/[0.04] px-4 py-1.5 type-subtext text-muted-foreground">
              <span>{g.label}</span>
              <span className="tabular-nums opacity-60">{g.tasks.length}</span>
            </div>
          ) : null}
          <WorkList rows={g.entries.map(toModel)} columns={cols} gutter />
        </div>
      ))}
    </div>
  );
}

/** Ask the global create bridge (`CreateMenuListener`) to open the issue
 *  composer preset to a status — the Issues status-section "+". */
function dispatchCreateInStatus(status: string) {
  window.dispatchEvent(
    new CustomEvent("excellent:create-task", { detail: { status } }),
  );
}
